import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    CheckCircle, XCircle, Eye
} from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { formatCurrency } from '../../lib/utils'

export default function WithdrawalManagement() {
    useTranslation()
    const { language } = useLanguageStore()

    const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending')
    const [selectedWithdrawal, setSelectedWithdrawal] = useState<any>(null)
    const [showModal, setShowModal] = useState(false)
    const [rejectReason, setRejectReason] = useState('')

    // Mock data
    const withdrawals = [
        {
            id: 1,
            ownerId: 5,
            ownerName: 'Nguyễn Văn Minh',
            ownerEmail: 'owner1@example.com',
            ownerVerified: true,
            amount: 5000000,
            bankName: 'Vietcombank',
            accountNumber: '0123456789',
            accountHolder: 'NGUYEN VAN MINH',
            requestDate: '2026-01-06T10:30:00',
            status: 'pending',
            walletBalance: 8500000,
            totalEarnings: 25000000,
            totalWithdrawn: 20000000,
        },
        {
            id: 2,
            ownerId: 6,
            ownerName: 'Trần Thị Hoa',
            ownerEmail: 'owner2@example.com',
            ownerVerified: true,
            amount: 3200000,
            bankName: 'Techcombank',
            accountNumber: '9876543210',
            accountHolder: 'TRAN THI HOA',
            requestDate: '2026-01-05T14:20:00',
            status: 'pending',
            walletBalance: 4500000,
            totalEarnings: 18000000,
            totalWithdrawn: 15000000,
        },
        {
            id: 3,
            ownerId: 7,
            ownerName: 'Lê Hoàng Nam',
            ownerEmail: 'owner3@example.com',
            ownerVerified: false,
            amount: 2000000,
            bankName: 'BIDV',
            accountNumber: '5555666677',
            accountHolder: 'LE HOANG NAM',
            requestDate: '2026-01-04T09:15:00',
            status: 'approved',
            walletBalance: 1200000,
            totalEarnings: 12000000,
            totalWithdrawn: 10000000,
            processedDate: '2026-01-04T15:30:00',
            processedBy: 'Admin',
        },
    ]

    const filteredWithdrawals = filter === 'all'
        ? withdrawals
        : withdrawals.filter(w => w.status === filter)

    const handleApprove = (withdrawal: any) => {
        setSelectedWithdrawal(withdrawal)
        // In real app, call API to approve
        alert(`Approved withdrawal #${withdrawal.id} for ${formatCurrency(withdrawal.amount, 'VND')}`)
    }

    const handleReject = (withdrawal: any) => {
        setSelectedWithdrawal(withdrawal)
        setShowModal(true)
    }

    const confirmReject = () => {
        // In real app, call API to reject
        alert(`Rejected withdrawal #${selectedWithdrawal.id}. Reason: ${rejectReason}`)
        setShowModal(false)
        setRejectReason('')
        setSelectedWithdrawal(null)
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-3xl font-bold">{language === 'en' ? 'Withdrawal Management' : 'Quản Lý Rút Tiền'}</h1>
                        <p className="text-gray-600">{language === 'en' ? 'Review and process withdrawal requests' : 'Xem xét và xử lý yêu cầu rút tiền'}</p>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                    <div className="card">
                        <p className="text-gray-600 text-sm">{language === 'en' ? 'Pending' : 'Chờ xử lý'}</p>
                        <p className="text-3xl font-bold text-orange-600 mt-1">2</p>
                    </div>
                    <div className="card">
                        <p className="text-gray-600 text-sm">{language === 'en' ? 'Approved Today' : 'Đã duyệt hôm nay'}</p>
                        <p className="text-3xl font-bold text-green-600 mt-1">5</p>
                    </div>
                    <div className="card">
                        <p className="text-gray-600 text-sm">{language === 'en' ? 'Total Pending' : 'Tổng chờ xử lý'}</p>
                        <p className="text-2xl font-bold text-orange-600 mt-1">{formatCurrency(8200000, 'VND')}</p>
                    </div>
                    <div className="card">
                        <p className="text-gray-600 text-sm">{language === 'en' ? 'Processed This Month' : 'Đã xử lý tháng này'}</p>
                        <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(45000000, 'VND')}</p>
                    </div>
                </div>

                {/* Filter Tabs */}
                <div className="flex gap-2 mb-6">
                    {(['all', 'pending', 'approved', 'rejected'] as const).map((status) => (
                        <button
                            key={status}
                            onClick={() => setFilter(status)}
                            className={`px-4 py-2 rounded-lg font-medium transition ${filter === status
                                ? 'bg-primary-600 text-white'
                                : 'bg-white hover:bg-gray-100'
                                }`}
                        >
                            {status === 'all' && (language === 'en' ? 'All' : 'Tất cả')}
                            {status === 'pending' && (language === 'en' ? 'Pending' : 'Chờ xử lý')}
                            {status === 'approved' && (language === 'en' ? 'Approved' : 'Đã duyệt')}
                            {status === 'rejected' && (language === 'en' ? 'Rejected' : 'Đã từ chối')}
                        </button>
                    ))}
                </div>

                {/* Withdrawals Table */}
                <div className="card overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'en' ? 'Owner' : 'Chủ xe'}</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'en' ? 'Amount' : 'Số tiền'}</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'en' ? 'Bank Info' : 'Thông tin NH'}</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'en' ? 'Request Date' : 'Ngày yêu cầu'}</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'en' ? 'Status' : 'Trạng thái'}</th>
                                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">{language === 'en' ? 'Actions' : 'Thao tác'}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y">
                            {filteredWithdrawals.map((withdrawal) => (
                                <tr key={withdrawal.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                                                {withdrawal.ownerName.charAt(0)}
                                            </div>
                                            <div>
                                                <p className="font-medium flex items-center gap-1">
                                                    {withdrawal.ownerName}
                                                    {withdrawal.ownerVerified && (
                                                        <span className="text-green-500">✓</span>
                                                    )}
                                                </p>
                                                <p className="text-sm text-gray-500">{withdrawal.ownerEmail}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4">
                                        <p className="font-bold text-lg">{formatCurrency(withdrawal.amount, 'VND')}</p>
                                        <p className="text-xs text-gray-500">
                                            {language === 'en' ? 'Balance' : 'Số dư'}: {formatCurrency(withdrawal.walletBalance, 'VND')}
                                        </p>
                                    </td>
                                    <td className="px-4 py-4">
                                        <p className="font-medium">{withdrawal.bankName}</p>
                                        <p className="text-sm text-gray-500">{withdrawal.accountNumber}</p>
                                        <p className="text-xs text-gray-400">{withdrawal.accountHolder}</p>
                                    </td>
                                    <td className="px-4 py-4">
                                        <p>{new Date(withdrawal.requestDate).toLocaleDateString()}</p>
                                        <p className="text-sm text-gray-500">{new Date(withdrawal.requestDate).toLocaleTimeString()}</p>
                                    </td>
                                    <td className="px-4 py-4">
                                        <span className={`badge ${withdrawal.status === 'pending' ? 'badge-warning' :
                                            withdrawal.status === 'approved' ? 'badge-success' : 'badge-danger'
                                            }`}>
                                            {withdrawal.status === 'pending' && (language === 'en' ? 'Pending' : 'Chờ xử lý')}
                                            {withdrawal.status === 'approved' && (language === 'en' ? 'Approved' : 'Đã duyệt')}
                                            {withdrawal.status === 'rejected' && (language === 'en' ? 'Rejected' : 'Đã từ chối')}
                                        </span>
                                    </td>
                                    <td className="px-4 py-4">
                                        {withdrawal.status === 'pending' ? (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleApprove(withdrawal)}
                                                    className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                                                    title={language === 'en' ? 'Approve' : 'Phê duyệt'}
                                                >
                                                    <CheckCircle className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleReject(withdrawal)}
                                                    className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                                                    title={language === 'en' ? 'Reject' : 'Từ chối'}
                                                >
                                                    <XCircle className="w-5 h-5" />
                                                </button>
                                                <button
                                                    className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition"
                                                    title={language === 'en' ? 'View Details' : 'Xem chi tiết'}
                                                >
                                                    <Eye className="w-5 h-5" />
                                                </button>
                                            </div>
                                        ) : (
                                            <button className="p-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition">
                                                <Eye className="w-5 h-5" />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Reject Modal */}
                {showModal && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl p-6 max-w-md w-full">
                            <h3 className="text-xl font-bold mb-4">{language === 'en' ? 'Reject Withdrawal' : 'Từ Chối Rút Tiền'}</h3>
                            <p className="text-gray-600 mb-4">
                                {language === 'en'
                                    ? `Are you sure you want to reject the withdrawal of ${formatCurrency(selectedWithdrawal?.amount || 0, 'VND')}?`
                                    : `Bạn có chắc muốn từ chối yêu cầu rút ${formatCurrency(selectedWithdrawal?.amount || 0, 'VND')}?`
                                }
                            </p>
                            <div className="mb-4">
                                <label className="block text-sm font-medium mb-1">
                                    {language === 'en' ? 'Reason (required)' : 'Lý do (bắt buộc)'}
                                </label>
                                <textarea
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    className="input h-24"
                                    placeholder={language === 'en' ? 'Enter rejection reason...' : 'Nhập lý do từ chối...'}
                                />
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="flex-1 py-2 border rounded-lg hover:bg-gray-50 transition"
                                >
                                    {language === 'en' ? 'Cancel' : 'Hủy'}
                                </button>
                                <button
                                    onClick={confirmReject}
                                    disabled={!rejectReason.trim()}
                                    className="flex-1 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-300 transition"
                                >
                                    {language === 'en' ? 'Reject' : 'Từ chối'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
